import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import {
  FormControl,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { tuiPure, TuiStringHandler, TuiContext } from '@taiga-ui/cdk';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, Language } from '@src/core';
import { AUTOCOMPLETE_TYPES } from '@src/constants';
import { AlertService, JobTitleService, OrganisationService, RoleService, UserService } from '@src/core/services';
import { Multimedia, JobTitleUI, UserUI, ViewMode, ContactUI } from '@src/models';
import { ContactsService, OrganisationRolesJobTitles, OrganisationView, RoleView } from '@src/api';
import { DateToTuiDateTimePipe } from '@src/app/shared/pipes';
import { isFileContact } from '@src/utils';
import { BaseResizableComponent } from '@src/app/modules/base';
import { formatPhone, inputPhoneFormValidator } from '@src/app/modules/phone';
import { CustomNamesService } from '@src/app/modules/custom-name-tabs';
import { SessionService } from '@src/app/modules/auth';
import { requireContactsValidator } from '@src/app/modules/contacts';
import { atLeastOneValidator } from '@src/app/validators';
import { InternalMessengerService } from '@src/app/modules/internal-messenger';

import { ARRAY_YEARS_FOR_HIDE, DEFAULT_YEAR_FOR_HIDE, MONTHS_ARRAY } from '../constants';
import { PermissionService } from '../services';

import { BirthMonth, FormData, FormDataControls, OrganisationRolesJobTitlesFormData } from './types';

@Component({
  selector: 'app-user-info-edit',
  templateUrl: './user-info-edit.component.html',
  styleUrls: ['./user-info-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserInfoEditComponent extends BaseResizableComponent implements OnChanges {
  private readonly permService = inject(PermissionService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly datePipe = inject(DatePipe);
  private readonly jobTitleService = inject(JobTitleService);
  private readonly roleService = inject(RoleService);
  private readonly userService = inject(UserService);
  private readonly sessionService = inject(SessionService);
  private readonly alertService = inject(AlertService);
  private readonly dateToTuiDateTimePipe = inject(DateToTuiDateTimePipe);
  private readonly customNameService = inject(CustomNamesService);
  private readonly translateService = inject(TranslateService);
  private readonly contactsService = inject(ContactsService);
  private readonly organisationService = inject(OrganisationService);
  private readonly internalMessengerService = inject(InternalMessengerService);

  @Input() mode: ViewMode = 'edit';

  @Input() data: UserUI | null = null;

  @Input() organisationId?: string | null;
  @Input() isParentOrganisation: boolean = false;
  @Input() searchByPhone = true;
  @Input() searchByEmail = true;

  @Output() saved: EventEmitter<UserUI> = new EventEmitter();
  @Output() canceled: EventEmitter<void> = new EventEmitter();

  readonly allowIsDefaultOrganisationEditing$ = this.permService.allowIsDefaultOrganisationEditing$;
  readonly allowSpecialFieldsEditing$ = this.permService.allowSpecialFieldsEditing$;
  readonly allowSpecialFieldsForAssociationEditing$ = this.permService.allowSpecialFieldsForAssociationEditing$;
  readonly allowJobTitleFieldsEditing$ = this.permService.allowJobTitleFieldsEditing$;
  readonly allowJobTitlesForAdminOnlyEditing$ = this.permService.allowJobTitlesForAdminOnlyEditing$;
  readonly allowAssociationEmployeeEditing$ = this.permService.allowAssociationEmployeeEditing$;
  readonly allowOrganisationEmployeeEditing$ = this.permService.allowOrganisationEmployeeEditing$;
  readonly allowEmailFieldEditing$ = this.permService.allowEmailFieldEditing$;
  readonly allowContactFieldCreating$ = this.permService.allowContactFieldCreating$;
  readonly allowContactFieldEditing$ = this.permService.allowContactFieldEditing$;
  readonly allowContactFieldDeleting$ = this.permService.allowContactFieldDeleting$;
  readonly allowOnlyYourShortProfileEditing$ = this.permService.allowOnlyYourShortProfileEditing$;
  readonly allowProfileByPhoneSearching$ = this.permService.allowProfileByPhoneSearching$;
  readonly allowProfileByEmailSearching$ = this.permService.allowProfileByEmailSearching$;
  readonly allowLoyaltyFieldsEditing$ = this.permService.allowLoyaltyFieldsEditing$;

  readonly maxDocFileSize = APP_CONFIG.fileSizeMax.doc;
  readonly autocompleteTypes = AUTOCOMPLETE_TYPES;

  loading: boolean = true;
  jobsForViewing$: BehaviorSubject<JobTitleUI[]> = this.jobTitleService.jobTitles$;
  jobsForEditing$: Observable<JobTitleUI[]> = this.jobTitleService.jobTitles$.pipe(
    map(jobTitles =>
      jobTitles.filter(jobTitle => !jobTitle.isAdminOnly || this.allowJobTitlesForAdminOnlyEditing$.value),
    ),
  );
  rolesForAssociation$?: Observable<RoleView[]>;
  rolesForOrganisation$?: Observable<RoleView[]>;
  newPhoto?: Multimedia;
  form!: FormGroup<FormDataControls>;
  authUser$ = this.userService.authUser$;
  currentParentOrganisationId?: string;
  organisation?: OrganisationView;

  /** Используется для полей, которых нет в this.form при добавлении существующего пользователя. this.form.disabled нельзя использовать из-за того, что новую организацию можно редактировать */
  formDisabled: boolean = false;

  months: BirthMonth[] = MONTHS_ARRAY;

  private searchMode: 'byPhone' | 'byEmail' | null = null;

  constructor() {
    super();

    this.initForm();
  }

  get organisations(): UntypedFormArray {
    return this.form.get('organisations') as UntypedFormArray;
  }

  get phoneControl(): UntypedFormControl {
    return this.form.get('phone') as UntypedFormControl;
  }

  get emailControl(): UntypedFormControl {
    return this.form.get('email') as UntypedFormControl;
  }

  get contactsControl() {
    return this.form.controls.contacts;
  }

  get allowSearchByPhone() {
    return this.searchByPhone && this.allowProfileByPhoneSearching$.value;
  }

  get allowSearchByEmail() {
    return this.searchByEmail && this.allowProfileByEmailSearching$.value;
  }

  // TODO: refactoring скорее всего нужно разнести логику создания и редактирования пользователя. в таком виде она непонятна
  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.mode) {
      this.initForm();
    }

    if (changes.organisationId && this.organisationId) {
      this.organisation = await this.organisationService.getOrganisationData(this.organisationId);
    }

    if (changes.data) {
      this.loading = true;
      this.resetAllData();

      if (this.mode === 'create') {
        this.addOrganisation({ isDefault: true, organisationId: this.organisationId as string }); // Добавили блок с текущей организацией и поставили метку дефолтной организации
      }

      if (this.data) {
        const mappedData = await this.mapUserUiToFormData(changes.data.currentValue);
        this.form.patchValue(mappedData);
      }

      await this.setContacts();
      this.loading = false;
    }

    if (changes.organisationId || changes.data) {
      this.rolesForAssociation$ = this.roleService.roles$.pipe(
        map(roles =>
          roles.filter(role => {
            if (!role.name) return false;
            return !['AdminDO', 'ParticipantDO'].includes(role.name); // TODO: строки заменить на RoleCode
          }),
        ),
      );

      this.rolesForOrganisation$ = this.roleService.roles$.pipe(
        map(roles =>
          roles.filter(role => {
            if (!role.name) return false;
            return !['AdminUO', 'ParticipantUO'].includes(role.name); // TODO: строки заменить на RoleCode
          }),
        ),
      );
    }

    this.cdr.markForCheck();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.currentParentOrganisationId = this.sessionService.currentParentOrganisationId;

    this.allowOnlyYourShortProfileEditing$
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(allowOnlyYourShortProfileEditing => {
        if (allowOnlyYourShortProfileEditing) {
          this.contactsControl.clearValidators();
        } else {
          this.contactsControl.setValidators([requireContactsValidator()]);
        }

        this.contactsControl.updateValueAndValidity({ onlySelf: false, emitEvent: false });
      });

    this.jobTitleService.getJobTitles();
    this.roleService.getRoles();
  }

  onClickSaveButton(): void {
    this.loading = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      if (this.form.get('email')?.errors?.email) {
        this.alertService.error(this.translateService.instant('common.alerts.errors.emailValidatorError'));
      } else if (this.form.errors?.['atLeastOne']) {
        this.alertService.error(
          this.translateService.instant('common.alerts.errors.fillAtLeastOne', {
            field1: this.translateService.instant('components.userInfo.fields.mobilePhone'),
            field2: this.translateService.instant('components.userInfo.fields.email'),
          }),
        );
      } else {
        this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      }
      this.loading = false;
      return;
    }

    const formData = this.mapFormDataToUserUI(this.form.getRawValue());
    this.newPhoto = undefined;

    this.saved.emit(formData);
  }

  onClickCancelButton(): void {
    this.newPhoto = undefined;
    this.canceled.emit();
  }

  onPhotoChange(newPhoto?: Multimedia): void {
    this.newPhoto = newPhoto;
    this.cdr.markForCheck();
  }

  onPhotoDeleted() {
    const userId = this.data?.id;

    if (userId) {
      this.internalMessengerService.userAvatarChanged(userId).subscribe();
    }
  }

  readonly stringifyJobTitle: TuiStringHandler<any | TuiContext<any>> = item => {
    return this.jobsForViewing$.value.find(jobs => jobs.jobTitleId === item)?.jobTitleName ?? '';
  };

  @tuiPure
  rolesStringify(items: ReadonlyArray<RoleView>): TuiStringHandler<TuiContext<string[]>> {
    const map = new Map(items.map(({ id, description }) => [id, description] as [string, string]));
    return ({ $implicit }: TuiContext<string[]>) => map.get($implicit[0]) || '';
  }

  @tuiPure
  monthStringify(months: readonly BirthMonth[]): TuiStringHandler<TuiContext<number>> {
    const map = new Map(months.map(({ id, name }) => [id, name] as [number, string]));
    return ({ $implicit }: TuiContext<number>) => this.translateService.instant(map.get($implicit) || ' ');
  }

  isDefaultOrganisation(index: number): boolean {
    const organisation = this.organisations?.at(index)?.value as OrganisationRolesJobTitlesFormData;
    return !!organisation?.isDefault;
  }

  setDefaultOrganisation(index: number): void {
    this.organisations.controls.forEach(organisationControl => {
      organisationControl.get('isDefault')?.patchValue(false);
    });
    const organisationControl = this.organisations.at(index);
    organisationControl.get('isDefault')?.patchValue(true);
    this.form.get('organisationId')?.patchValue(organisationControl.get('organisationId')?.value);
  }

  getAllowJobTitlesFieldsEditing(organisationId?: string | null) {
    return (
      this.allowJobTitleFieldsEditing$.value &&
      (this.allowAssociationEmployeeEditing$.value ||
        (this.allowOrganisationEmployeeEditing$.value && organisationId === this.organisationId) ||
        this.authUser$.value?.id === this.data?.id)
    );
  }

  private initForm(): void {
    this.form = new FormGroup<FormDataControls>(
      {
        id: new UntypedFormControl(undefined),
        firstName: new UntypedFormControl(undefined, [Validators.required, Validators.maxLength(50)]),
        middleName: new UntypedFormControl(undefined, Validators.maxLength(50)),
        lastName: new UntypedFormControl(undefined, [Validators.required, Validators.maxLength(50)]),
        phone: new UntypedFormControl(
          undefined,
          this.allowSpecialFieldsEditing$.value ? [inputPhoneFormValidator()] : [],
        ),
        email: new UntypedFormControl(undefined, [Validators.email]),
        organisationId: new UntypedFormControl(undefined),
        jobDescription: new UntypedFormControl(undefined),
        organisations: this.formBuilder.array([]),
        birthDate: new UntypedFormControl(undefined),
        birthDateDay: new UntypedFormControl(undefined),
        birthDateMonth: new UntypedFormControl(undefined),
        birthDateYear: new UntypedFormControl(undefined),

        city: new UntypedFormControl(undefined),
        favoriteCategories: new UntypedFormControl(undefined),

        resources: new UntypedFormControl(undefined),
        hobbies: new UntypedFormControl(undefined),
        isFeePaid: new UntypedFormControl(false),
        paymentDetails: new UntypedFormControl(undefined),
        oldPaymentDetails: new UntypedFormControl(null),
        authorityValidTill: new UntypedFormControl(undefined),
        contacts: new FormControl<ContactUI[]>([], { nonNullable: true, validators: requireContactsValidator() }), // TODO: переписать на нормальный тип когда будем избавляться от UntypedFormControl

        curator: new UntypedFormControl(null),
      },
      { validators: atLeastOneValidator('phone', 'email') },
    );

    this.form
      .get('phone')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(async phone => {
        const normalizedPhone = phone?.replace(/\D/g, '');

        if (
          this.allowSearchByPhone &&
          this.mode === 'create' &&
          this.data?.phone?.replace(/\D/g, '') !== normalizedPhone &&
          this.searchMode !== 'byEmail'
        ) {
          if (phone && this.phoneControl.valid) {
            this.userService
              .getUserByPhone(normalizedPhone)
              .pipe(takeUntil(this.destroyed$$))
              .subscribe(profileByPhone => {
                this.onProfileFound(profileByPhone, this.phoneControl, 'byPhone');
                return;
              });
          }

          if (this.formDisabled) {
            this.resetAllData(this.phoneControl.value);
            this.restoreForm();
          }
        }

        this.cdr.markForCheck();
      });

    this.form
      .get('email')
      ?.valueChanges.pipe(takeUntil(this.destroyed$$))
      .subscribe(async email => {
        if (
          this.allowSearchByEmail &&
          this.mode === 'create' &&
          this.data?.email !== email &&
          this.searchMode !== 'byPhone'
        ) {
          if (email && this.emailControl.valid) {
            this.userService
              .getUserByEmail(email)
              .pipe(takeUntil(this.destroyed$$))
              .subscribe(profileByEmail => {
                this.onProfileFound(profileByEmail, this.emailControl, 'byEmail');
                return;
              });
          }

          if (this.formDisabled) {
            this.resetAllData(undefined, this.emailControl?.value);
            this.restoreForm();
          }
        }

        this.cdr.markForCheck();
      });
  }

  private async onProfileFound(profile: UserUI, control: UntypedFormControl, searchMode: 'byEmail' | 'byPhone') {
    if (profile && !this.loading) {
      this.loading = true;

      if (!!profile.organisations?.find(organisation => organisation.organisationId === this.organisationId)) {
        this.alertService.error(
          this.translateService.instant('components.userInfo.alerts.errors.userEmailAlreadyExist'),
          0,
        );

        this.loading = false;
        this.cdr.markForCheck();
        return;
      }

      if (this.formDisabled) {
        this.resetAllData(undefined, control?.value);
      } else {
        this.removeAllOrganisations();
      }

      this.searchMode = searchMode;
      this.data = profile;

      const mappedData = await this.mapUserUiToFormData(profile);
      this.form.patchValue(mappedData);
      this.form.disable();
      control.enable();
      this.formDisabled = true;

      await this.addOrganisation({ isDefault: false }); // Добавили блок с текущей организацией
      await this.setContacts();

      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private async mapUserUiToFormData(user: UserUI) {
    const {
      id,
      firstName,
      lastName,
      middleName,
      email,
      resources,
      hobbies,
      isFeePaid,
      paymentDetailsId,
      curatorId,
      organisations,
      city,
      favoriteCategories,
    } = user;
    let curatorFullName = '';

    let phone: string | undefined = '79000000000'; // TODO: HACK for edit user api

    if (this.allowSpecialFieldsEditing$.value) {
      phone = formatPhone(user.phone);
    }

    if (organisations) {
      for (const organisation of organisations) {
        await this.addOrganisation(organisation, user);
      }
    }

    const birthDate = user.birthDate ? new Date(user.birthDate) : undefined;
    const birthDateYear =
      birthDate && !ARRAY_YEARS_FOR_HIDE.includes(birthDate.getFullYear()) ? birthDate?.getFullYear() : undefined;
    const birthDateString = birthDate
      ? birthDateYear
        ? this.datePipe.transform(birthDate, 'dd MMMM y')
        : this.datePipe.transform(birthDate, 'dd MMMM')
      : undefined;

    const authorityValidTill = this.dateToTuiDateTimePipe.transform(user.authorityValidTill, 'date');

    const oldPaymentDetails = paymentDetailsId
      ? { id: paymentDetailsId, name: this.customNameService.getItem('label.userProfileAttachment')?.title }
      : null;

    if (curatorId) {
      const curator = await lastValueFrom(this.userService.getUserData(curatorId));
      curatorFullName = curator?.fullName ?? '';
    }

    return <FormData>{
      id,
      firstName,
      middleName,
      lastName,
      phone,
      email,
      birthDate: birthDateString,
      birthDateDay: birthDate?.getDate(),
      birthDateMonth: birthDate ? birthDate.getMonth() + 1 : undefined,
      birthDateYear,
      city,
      favoriteCategories,
      resources,
      hobbies,
      isFeePaid,
      authorityValidTill,
      oldPaymentDetails,
      curator: curatorId ? { id: curatorId, fullName: curatorFullName } : undefined, // TODO: Заменить { ... } на curator после решения задачи #10334
    };
  }

  private async restoreForm() {
    this.data = null;

    await this.addOrganisation({ isDefault: true });
    await this.setContacts();

    this.loading = false;
  }

  private mapFormDataToUserUI(formData: FormData) {
    const {
      id,
      firstName,
      middleName,
      lastName,
      email,
      jobDescription,
      birthDateDay,
      resources,
      hobbies,
      isFeePaid,
      paymentDetails,
      oldPaymentDetails,
      curator,
      city,
      favoriteCategories,
    } = formData;

    let organisationsToAdd: OrganisationRolesJobTitles[] | undefined;

    const birthDateMonth = formData.birthDateMonth;
    const birthDateYear = formData.birthDateYear ?? DEFAULT_YEAR_FOR_HIDE;
    const birthDate =
      birthDateDay && birthDateMonth ? new Date(birthDateYear, birthDateMonth - 1, birthDateDay, 0, 0, 0) : null;

    const authorityValidTill = formData.authorityValidTill
      ? new Date(formData.authorityValidTill.year, formData.authorityValidTill.month, formData.authorityValidTill.day)
      : undefined;

    // TODO: refactoring
    let organisations = formData.organisations.map(organisation => {
      return <OrganisationRolesJobTitles>{
        organisationId: organisation.organisationId,
        isDefault: organisation.isDefault,
        jobTitles: organisation.jobTitlesIds?.length
          ? organisation.jobTitlesIds.map(jobTitleId => {
              return {
                id: jobTitleId,
                description: organisation.jobTitlesDescription,
              };
            })
          : [{ description: organisation.jobTitlesDescription }],
        roleIds: organisation.roleIds,
      };
    });

    if (this.mode === 'create' && this.searchMode) {
      const organisationToAdd = organisations.find(organisation => {
        return organisation.organisationId === this.organisationId;
      });

      if (organisationToAdd) {
        organisationsToAdd = [organisationToAdd];
      }

      organisations = organisations.filter(organisation => organisation.organisationId !== this.organisationId);
    }

    const contacts = formData.contacts.map((contact: ContactUI) => {
      if (!contact.id) {
        delete contact.id;
      }

      delete contact.contactListValues;

      contact.description = contact.description?.trim(); // TODO: перенести в Interceptor #11548
      return contact;
    });

    return <UserUI>{
      id,
      firstName,
      middleName,
      lastName,
      phone: formData.phone,
      email,
      birthDate: birthDate ? formatDate(birthDate, 'yyyy-MM-dd', Language.EN) : null,
      organisations,
      jobTitleDescription: jobDescription, // TODO: fix bug. need refactoring
      photo: this.newPhoto?.file,

      cityId: city?.cityId,
      city: city,
      favoriteCategories,

      resources,
      hobbies,
      isFeePaid: isFeePaid !== null ? isFeePaid : undefined,
      authorityValidTill,
      paymentDetails,
      deletePaymentDetailsId: !oldPaymentDetails && this.data?.paymentDetailsId ? this.data.paymentDetailsId : null,

      curatorId: curator?.id,
      organisationsToAdd,
      contacts,
    };
  }

  private async createOrganisation(
    organisation?: OrganisationRolesJobTitles,
    user?: UserUI,
  ): Promise<UntypedFormGroup> {
    const organisationJobTitle = user?.organisationJobTitles?.find(
      organisationJobTitle => organisationJobTitle.organisationId === organisation?.organisationId,
    );
    const organisationName = organisationJobTitle
      ? organisationJobTitle.organisationName
      : this.organisation?.shortName;

    let roleIds: string[] | undefined = ['00000000-0000-0000-0000-000000000001']; // TODO: HACK for edit user api
    let jobTitles: JobTitleUI[] = [];

    if (this.allowSpecialFieldsEditing$.value && organisation?.organisationId && user?.id) {
      const userRoles = await this.roleService.getUserRolesData(user.id, organisation.organisationId);
      roleIds = userRoles[0]?.id ? [userRoles[0].id] : undefined;
    }

    if (user && this.allowSpecialFieldsForAssociationEditing$.value) {
      jobTitles = this.jobsForViewing$.value.filter(
        job =>
          user.organisationJobTitles &&
          user.organisationJobTitles.findIndex(
            organisationJobTitle => organisationJobTitle.jobTitleId === job.jobTitleId,
          ) > -1,
      );
    }

    const jobTitlesIds: string[] | undefined = organisation?.jobTitles
      ?.filter(jobTitle => Boolean(jobTitle.id))
      .map(jobTitle => jobTitle.id!);
    const jobTitlesDescription = organisation?.jobTitles?.[0].description ?? '';

    return this.formBuilder.group({
      organisationId: new UntypedFormControl(organisation?.organisationId ?? this.organisationId),
      organisationName: new UntypedFormControl(organisationName),
      jobTitles: new UntypedFormControl(jobTitles),
      jobTitlesIds: new UntypedFormControl(
        jobTitlesIds,
        this.getAllowJobTitlesFieldsEditing(organisation?.organisationId ?? this.organisationId)
          ? Validators.required
          : undefined,
      ),
      jobTitlesDescription: new UntypedFormControl(jobTitlesDescription),
      roleIds: new UntypedFormControl(roleIds, this.allowSpecialFieldsEditing$.value ? Validators.required : undefined),
      isDefault: new UntypedFormControl(organisation?.isDefault),
    });
  }

  private async addOrganisation(organisation?: OrganisationRolesJobTitles, user?: UserUI): Promise<void> {
    if (
      this.allowAssociationEmployeeEditing$.value ||
      (this.allowOrganisationEmployeeEditing$.value && organisation?.organisationId === this.organisationId) ||
      user?.id === this.authUser$?.value?.id
    ) {
      const organisationFormGroup = await this.createOrganisation(organisation, user);
      if (this.mode === 'create' && organisationFormGroup.get('organisationId')?.value !== this.organisationId) {
        organisationFormGroup.disable();
      }
      this.organisations.push(organisationFormGroup);
      this.cdr.markForCheck();
    }
  }

  private removeAllOrganisations(): void {
    for (let i = this.organisations.length - 1; i >= 0; i--) {
      this.organisations.removeAt(i);
    }
  }

  private normalizeContacts(contacts: ContactUI[]) {
    return contacts.map((contact: ContactUI) => {
      if (isFileContact(contact.contactTypeId) && contact.contact) {
        contact.oldDocument = {
          id: contact.contact,
          name: contact.description ?? this.translateService.instant('components.userInfo.labels.file'),
        };
      }

      contact.description = contact.description?.trim();
      return contact;
    });
  }

  private async setContacts() {
    const parentOrganisationId = this.userService.authUser$.value?.parentOrganisationId;
    if (parentOrganisationId) {
      const contactsForUnion = await lastValueFrom(this.contactsService.getUserContactsForUnion(parentOrganisationId));
      const contactsForUser = this.data?.id ? await this.userService.getUserContactsData(this.data.id) : [];

      const contacts = this.mergeContacts(
        this.normalizeContacts(contactsForUnion),
        this.normalizeContacts(contactsForUser),
      );

      this.form.patchValue({ contacts });
    }
  }

  // TODO: Перенести логику на бэк #11547
  private mergeContacts(unionContacts: ContactUI[], userContacts: ContactUI[] = []) {
    const newContacts = unionContacts.filter(unionContact => {
      return !userContacts.find(userContact => {
        return (
          userContact.contactTypeId === unionContact.contactTypeId &&
          userContact.description === unionContact.description
        );
      });
    });

    const contacts = userContacts.concat(newContacts);

    return contacts;
  }

  private resetAllData(phone?: string, email?: string) {
    this.searchMode = null;
    this.newPhoto = undefined;
    this.form.enable({ emitEvent: false });
    this.formDisabled = false;
    this.form.reset({ phone, email, isFeePaid: false }, { emitEvent: false });
    this.removeAllOrganisations();
  }
}
